import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import moment from 'moment';
import { ReactNode, useState } from 'react';
import { UseQueryResult } from 'react-query';
import styled from 'styled-components';
import { SectionHeaderText } from '../../components/Common';
import { useDevicePackets, useDevicePositions } from '../../hooks/useDeviceData';
import {
  Device,
  DeviceDataPage,
  DevicePacketRecord,
  DevicePositionRecord,
} from '../../models/Device';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const TableRegion = styled.div`
  position: relative;
  min-height: 120px;
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;
`;

const JsonBlock = styled.pre`
  box-sizing: border-box;
  margin: 0;
  padding: 12px;
  max-height: 60vh;
  overflow: auto;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  background: #f5f5f5;
  border-radius: 4px;
  font-family: "Roboto Mono", "Lucida Console", Monaco, monospace;
  font-size: 0.75rem;
`;

interface Column<T> {
  label: string;
  render: (row: T) => ReactNode;
}

interface DeviceDataSectionProps<T extends { id: string; payload: unknown }> {
  title: string;
  emptyLabel: string;
  detailTitle: string;
  columns: Column<T>[];
  query: UseQueryResult<DeviceDataPage<T>, unknown>;
  page: number;
  canGoPrevious: boolean;
  onPrevious: () => void;
  onNext: (cursor: string) => void;
}

function DeviceDataSection<T extends { id: string; payload: unknown }>({
  title,
  emptyLabel,
  detailTitle,
  columns,
  query,
  page,
  canGoPrevious,
  onPrevious,
  onNext,
}: DeviceDataSectionProps<T>) {
  const [selected, setSelected] = useState<T | null>(null);
  const nextCursor = query.data?.pageInfo.nextCursor;

  return (
    <section aria-label={title}>
      <SectionHeaderText>
        {title}
        <IconButton
          color="primary"
          size="small"
          sx={{ marginLeft: 'auto' }}
          aria-label={`Refresh ${title.toLowerCase()}`}
          disabled={query.isFetching}
          onClick={() => query.refetch()}
        >
          {query.isFetching ? <CircularProgress size={24} color="inherit" /> : <RefreshIcon />}
        </IconButton>
      </SectionHeaderText>
      <TableRegion aria-live="polite">
        {query.isLoading ? (
          <CircularProgress size={28} aria-label={`Loading ${title.toLowerCase()}`} />
        ) : query.isError ? (
          <Typography color="error">Unable to load {title.toLowerCase()}.</Typography>
        ) : !query.data?.data.length ? (
          <Typography>{emptyLabel}</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" aria-label={`Recent ${title.toLowerCase()}`}>
              <TableHead
                sx={{
                  backgroundColor: '#dfe3e8',
                  '& .MuiTableCell-head': {
                    borderBottom: '2px solid #b8c0ca',
                    fontWeight: 700,
                  },
                }}
              >
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.label}>{column.label}</TableCell>
                  ))}
                  <TableCell align="right">Payload</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ opacity: query.isFetching ? 0.55 : 1 }}>
                {query.data.data.map((row) => (
                  <TableRow
                    hover
                    key={row.id}
                    onClick={(event) => {
                      if (event.target instanceof Element && event.target.closest('a,button')) return;
                      setSelected(row);
                    }}
                    sx={{ cursor: 'pointer' }}
                  >
                    {columns.map((column) => (
                      <TableCell key={column.label} sx={{ whiteSpace: 'nowrap' }}>
                        {column.render(row)}
                      </TableCell>
                    ))}
                    <TableCell align="right">
                      <Button
                        size="small"
                        aria-label={`View ${detailTitle}`}
                        onClick={() => setSelected(row)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TableRegion>

      <Pagination role="navigation" aria-label={`${title} pagination`}>
        <Button variant="outlined" onClick={onPrevious} disabled={!canGoPrevious || query.isFetching}>
          Previous
        </Button>
        <Typography variant="body2">Page {page}</Typography>
        <Button
          variant="outlined"
          onClick={() => nextCursor && onNext(nextCursor)}
          disabled={!nextCursor || query.isFetching}
        >
          Next
        </Button>
      </Pagination>

      <Dialog open={!!selected} onClose={() => setSelected(null)} fullWidth maxWidth="md">
        <DialogTitle>{detailTitle}</DialogTitle>
        <DialogContent>
          <JsonBlock>{selected ? JSON.stringify(selected.payload, null, 2) : ''}</JsonBlock>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)} autoFocus>Close</Button>
        </DialogActions>
      </Dialog>
    </section>
  );
}

const formatTimestamp = (timestamp?: string | null) => {
  if (!timestamp) return '--';

  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? timestamp : date.toLocaleString();
};

const Timestamp = ({ value }: { value?: string | null }) => {
  if (!value) return <>--</>;

  return (
    <Tooltip arrow title={moment(value).fromNow()}>
      <span>{formatTimestamp(value)}</span>
    </Tooltip>
  );
};

const formatNumber = (value?: number | null, suffix = '') =>
  value === undefined || value === null ? '--' : `${value}${suffix}`;

const PositionLink = ({ latitude, longitude }: { latitude: number; longitude: number }) => {
  const label = `${latitude.toFixed(2)}, ${longitude.toFixed(2)}`;
  const url = `https://www.google.com/maps?q=${latitude},${longitude}`;

  return <a href={url} target="_blank" rel="noopener noreferrer">{label}</a>;
};

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;

const formatPacketPositionField = (
  packet: DevicePacketRecord,
  field: 'numSats' | 'gpsType' | 'positioningSource' | 'gpsPositioned' | 'gnssAccuracy' | 'course',
) => {
  const content = asRecord(asRecord(packet.payload)?.content);
  const value = field === 'numSats'
    ? content?.numSats
    : asRecord(content?.courseStatus)?.[field];

  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number' && Number.isFinite(value)) {
    return field === 'course' ? `${value}°` : value;
  }
  return typeof value === 'string' && value.trim() ? value : '--';
};

const packetColumns: Column<DevicePacketRecord>[] = [
  { label: 'Received', render: (packet) => <Timestamp value={packet.createdAt} /> },
  { label: 'Type', render: (packet) => packet.type || '--' },
  { label: 'Device time', render: (packet) => <Timestamp value={packet.eventAt} /> },
  { label: 'Satellites', render: (packet) => formatPacketPositionField(packet, 'numSats') },
  {
    label: 'GSM signal (raw)',
    render: (packet) => {
      const signal = asRecord(asRecord(packet.payload)?.content)?.gsmSignal;
      return typeof signal === 'number' && Number.isFinite(signal) ? signal : '--';
    },
  },
  { label: 'GPS type', render: (packet) => formatPacketPositionField(packet, 'gpsType') },
  { label: 'Positioning source', render: (packet) => formatPacketPositionField(packet, 'positioningSource') },
  { label: 'GPS positioned', render: (packet) => formatPacketPositionField(packet, 'gpsPositioned') },
  { label: 'GNSS accuracy', render: (packet) => formatPacketPositionField(packet, 'gnssAccuracy') },
  { label: 'Course', render: (packet) => formatPacketPositionField(packet, 'course') },
];

const positionColumns: Column<DevicePositionRecord>[] = [
  { label: 'Received', render: (position) => <Timestamp value={position.createdAt} /> },
  { label: 'Position time', render: (position) => <Timestamp value={position.positionAt} /> },
  {
    label: 'Location',
    render: (position) => <PositionLink latitude={position.latitude} longitude={position.longitude} />,
  },
  { label: 'Speed (km/h)', render: (position) => formatNumber(position.speedKph) },
  { label: 'Battery', render: (position) => formatNumber(position.batteryPercent, '%') },
];

function useCursorHistory() {
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);

  return {
    cursor: cursors[cursors.length - 1],
    page: cursors.length,
    canGoPrevious: cursors.length > 1,
    goPrevious: () => setCursors((current) => current.length > 1 ? current.slice(0, -1) : current),
    goNext: (cursor: string) => setCursors((current) => [...current, cursor]),
  };
}

export function DeviceDataTab({ device }: { device: Device }) {
  const packetPagination = useCursorHistory();
  const positionPagination = useCursorHistory();
  const packets = useDevicePackets(device.imei, packetPagination.cursor);
  const positions = useDevicePositions(device.imei, positionPagination.cursor);

  return (
    <Container>
      <DeviceDataSection
        title="Packets"
        emptyLabel="No packets received."
        detailTitle="Packet JSON"
        columns={packetColumns}
        query={packets}
        page={packetPagination.page}
        canGoPrevious={packetPagination.canGoPrevious}
        onPrevious={packetPagination.goPrevious}
        onNext={packetPagination.goNext}
      />
      <DeviceDataSection
        title="Positions"
        emptyLabel="No positions received."
        detailTitle="Position JSON"
        columns={positionColumns}
        query={positions}
        page={positionPagination.page}
        canGoPrevious={positionPagination.canGoPrevious}
        onPrevious={positionPagination.goPrevious}
        onNext={positionPagination.goNext}
      />
    </Container>
  );
}
